import { useQuery } from 'react-query';

import { useCompanyId } from 'modules/app/hooks/useCompanyId';
import { getV2ApiCompanyIdTravelTripsTripIdPayments } from 'src/client/spendesk-api';
import type {
  FullResponse,
  GetV2ApiCompanyIdTravelTripsTripIdPaymentsResponseOK,
  GetV2ApiCompanyIdTravelTripsTripIdPaymentsResponses,
} from 'src/client/spendesk-api-types';

const POLLING_INTERVAL = 2000;

const TERMINAL_STATUSES = [
  'partial_failure',
  'failed',
  'expired',
  'partial_success',
  'success',
];
const SUCCESS_STATUSES = ['partial_success', 'success'];
const ERROR_STATUSES = ['partial_failure', 'failed', 'expired'];

export const usePaymentStatusQuery = (tripId: string | null) => {
  const companyId = useCompanyId();

  const query = useQuery(
    ['tripPaymentStatus', companyId, tripId],
    () => {
      if (!tripId) {
        throw new Error('tripId is required');
      }
      return getV2ApiCompanyIdTravelTripsTripIdPayments({
        path: {
          companyId,
          tripId,
        },
      });
    },
    {
      enabled: Boolean(tripId),
      refetchInterval: (data) => {
        if (
          isSuccessResponse(data) &&
          TERMINAL_STATUSES.includes(data.body.data.status)
        ) {
          return false;
        }
        return POLLING_INTERVAL;
      },
      refetchIntervalInBackground: true,
    },
  );

  const paymentStatus = isSuccessResponse(query.data)
    ? (query.data.body.data
        .status as GetV2ApiCompanyIdTravelTripsTripIdPaymentsResponseOK['data']['status'])
    : null;

  return {
    ...query,
    paymentStatus,
    isPaymentSuccessful:
      paymentStatus && SUCCESS_STATUSES.includes(paymentStatus),
    isPaymentExpired: paymentStatus === 'expired',
    isPaymentError: paymentStatus && ERROR_STATUSES.includes(paymentStatus),
  };
};

const isSuccessResponse = (
  data: GetV2ApiCompanyIdTravelTripsTripIdPaymentsResponses | undefined,
): data is FullResponse<
  GetV2ApiCompanyIdTravelTripsTripIdPaymentsResponseOK,
  200
> => {
  return data?.statusCode === 200;
};
